import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";

import Home from "@/pages/Home";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";

import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import Checker from "@/pages/Checker";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";

import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import AdminProtected from "@/components/AdminProtected";

// ⭐ Newly added pages (important for Razorpay)
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import Shipping from "@/pages/Shipping";
import Contact from "@/pages/Contact";
import SentenceCorrectorGuide from "@/pages/blog/SentenceCorrectorGuide";


import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user session
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser({ ...response.data, token: localStorage.getItem("token") });
    } catch (error) {
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser({ ...userData, token });
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, fetchUser }}>
      <BrowserRouter>
        <div className="App">
          <Routes>

            {/* Public Pages */}
            <Route path="/" element={<Home />} />
            <Route
  path="/ai-sentence-corrector/how-to"
  element={<SentenceCorrectorGuide />}
/>
            {/* Auth */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />

            {/* Password Reset System */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Grammar Tool */}
            <Route
              path="/checker"
              element={user ? <Checker /> : <Navigate to="/signup" />}
            />

            {/* User Pages */}
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/login" />}
            />

            <Route
              path="/history"
              element={user ? <History /> : <Navigate to="/login" />}
            />

            <Route path="/pricing" element={<Pricing />} />

            <Route
              path="/payment-success"
              element={user ? <PaymentSuccess /> : <Navigate to="/login" />}
            />

            {/* Admin Routes */}
            <Route path="/starmoonlight/login" element={<AdminLogin />} />

            <Route
              path="/starmoonlight/panel"
              element={
                <AdminProtected>
                  <AdminDashboard />
                </AdminProtected>
              }
            />

            {/* ⭐ Razorpay-required policy pages */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/contact" element={<Contact />} />

          </Routes>

          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
